import { Minus, TrendingDown, TrendingUp } from "lucide-react";
import { cn } from "@/lib/utils";

export type Delta = { pct: number; dir: "up" | "down" | "flat" } | null;

/**
 * Percentage change of a metric versus the previous period. Returns null when
 * there's no comparable baseline (no previous data, or previous was zero), so
 * callers simply omit the delta instead of showing a misleading ∞%.
 */
export function computeDelta(
  current: number,
  previous: number | undefined
): Delta {
  if (previous === undefined || previous === 0) return null;
  const pct = ((current - previous) / previous) * 100;
  if (Math.abs(pct) < 0.5) return { pct: 0, dir: "flat" };
  return { pct, dir: pct > 0 ? "up" : "down" };
}

/**
 * Inline period-over-period delta: a trending arrow + signed percentage. Matches
 * the dashboard KPI cards (TrendingUp/TrendingDown/Minus, success/destructive
 * colours, one-decimal signed %) so both surfaces read the same. Size is
 * controlled by the caller via `className`. Higher is always treated as good
 * (green) for the metrics on this page — revenue, orders, basket, tips.
 */
export function DeltaText({
  delta,
  className,
}: {
  delta: Delta;
  className?: string;
}) {
  if (!delta) return null;

  if (delta.dir === "flat") {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-0.5 font-semibold text-muted-foreground",
          className
        )}
        title="Stable vs période précédente"
      >
        <Minus className="h-[1em] w-[1em]" />
        0%
      </span>
    );
  }

  const up = delta.dir === "up";
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 font-semibold",
        up ? "text-success" : "text-destructive",
        className
      )}
      title="Vs période précédente"
    >
      <Icon className="h-[1em] w-[1em]" />
      {up ? "+" : "-"}
      {Math.abs(delta.pct).toFixed(1)}%
    </span>
  );
}
